// Convenios (prepagadas) de Farmacia Interna (Fase 3-4, 2026-09-11) — mismo
// motor que /admin/convenios del CRM (lib/facturacion-convenio.ts). Los
// convenios (RUC, activo/inactivo) son TENANT-WIDE a propósito: Saludsa es
// la misma aseguradora la facture el CRM o farmacia. Lo que sí se acota a
// farmacia es qué copagos de HOY aparecen como pendientes de facturar
// (cajaDelDiaFarmacia.porCobrarConvenio) y qué facturas a convenio ya
// emitidas se listan acá — un XML = un comprador, el copago nunca se suma.
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  actualizarConvenioFarmacia,
  cajaDelDiaFarmacia,
  facturarConvenioFarmacia,
  listarConveniosFarmacia,
  listarFacturasConvenioFarmacia,
  type CajaPendienteFarmacia,
  type ConvenioFarmacia,
  type FacturaConvenioFarmacia,
} from '@/lib/farmaciaInterna';

function formatoUsd(cents: number): string {
  return (cents / 100).toLocaleString('es-EC', { style: 'currency', currency: 'USD' });
}

export function ConveniosFarmacia(): JSX.Element {
  const { t } = useTranslation();
  const [convenios, setConvenios] = useState<ConvenioFarmacia[]>();
  const [disponible, setDisponible] = useState(true);
  const [pendientes, setPendientes] = useState<CajaPendienteFarmacia[]>([]);
  const [facturas, setFacturas] = useState<FacturaConvenioFarmacia[]>([]);
  const [rucEditado, setRucEditado] = useState<Record<number, string>>({});
  const [guardandoId, setGuardandoId] = useState<number>();

  const [facturando, setFacturando] = useState<string>();
  const [convenioElegido, setConvenioElegido] = useState<Record<string, number>>({});
  const [paciente, setPaciente] = useState('');
  const [prefacturaRef, setPrefacturaRef] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string>();
  const [aviso, setAviso] = useState<string>();

  async function recargar(): Promise<void> {
    const [c, caja, f] = await Promise.all([listarConveniosFarmacia(), cajaDelDiaFarmacia(), listarFacturasConvenioFarmacia()]);
    setConvenios(c.convenios);
    setDisponible(c.disponible);
    setPendientes(caja.porCobrarConvenio.filter((p) => !p.facturadoConvenio));
    setFacturas(f.facturas);
  }

  useEffect(() => {
    void recargar();
  }, []);

  async function guardarRuc(convenio: ConvenioFarmacia): Promise<void> {
    const ruc = (rucEditado[convenio.id] ?? convenio.ruc ?? '').trim();
    setGuardandoId(convenio.id);
    setError(undefined);
    try {
      const r = await actualizarConvenioFarmacia(convenio.id, { ruc: ruc || null });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      await recargar();
    } finally {
      setGuardandoId(undefined);
    }
  }

  async function alternarActivo(convenio: ConvenioFarmacia): Promise<void> {
    const r = await actualizarConvenioFarmacia(convenio.id, { active: !convenio.active });
    if (!r.ok) {
      setError(r.error);
      return;
    }
    await recargar();
  }

  function abrirFacturar(pendiente: CajaPendienteFarmacia): void {
    setFacturando(pendiente.ref);
    setPaciente(pendiente.contactName);
    setPrefacturaRef('');
    setError(undefined);
  }

  async function confirmarFacturar(pendiente: CajaPendienteFarmacia): Promise<void> {
    const convenioId = convenioElegido[pendiente.ref];
    if (!convenioId) {
      setError(t('farmaciaApp.convenios.elijaConvenio'));
      return;
    }
    if (!paciente.trim()) {
      setError(t('farmaciaApp.convenios.pacienteRequerido'));
      return;
    }
    setEnviando(true);
    setError(undefined);
    try {
      const r = await facturarConvenioFarmacia({
        paymentId: Number(pendiente.ref),
        convenioId,
        coveredAmountCents: pendiente.amountCents ?? 0,
        paciente: paciente.trim(),
        prefacturaRef: prefacturaRef.trim() || undefined,
      });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setAviso(t('farmaciaApp.convenios.facturada', { convenio: r.datos.convenioName, monto: (r.datos.totalCents / 100).toFixed(2) }));
      setFacturando(undefined);
      await recargar();
    } finally {
      setEnviando(false);
    }
  }

  if (convenios === undefined) {
    return <p className="text-sm text-hce-muted">{t('farmaciaApp.cargando')}</p>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {aviso && (
        <Alert>
          <AlertDescription>{aviso}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <p className="text-sm font-semibold text-hce-ink">{t('farmaciaApp.convenios.listaTitulo')}</p>
        {!disponible ? (
          <p className="text-sm text-hce-muted">{t('farmaciaApp.noDisponible')}</p>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {convenios.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 text-sm">
                <span className="min-w-0 flex-1">
                  <span className={`block ${c.active ? 'text-hce-ink' : 'text-hce-muted line-through'}`}>{c.name}</span>
                  {!c.puedeFacturar && <span className="block text-xs text-amber-700">{t('farmaciaApp.convenios.sinRuc')}</span>}
                </span>
                <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                  <Input
                    className="h-8 w-40"
                    placeholder={t('farmaciaApp.convenios.rucPlaceholder')}
                    value={rucEditado[c.id] ?? c.ruc ?? ''}
                    onChange={(e) => setRucEditado((prev) => ({ ...prev, [c.id]: e.target.value }))}
                  />
                  <Button size="sm" variant="secondary" disabled={guardandoId === c.id} onClick={() => void guardarRuc(c)}>
                    {t('farmaciaApp.convenios.guardarRuc')}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-xs" onClick={() => void alternarActivo(c)}>
                    {c.active ? t('farmaciaApp.catalogo.desactivar') : t('farmaciaApp.catalogo.activar')}
                  </Button>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-hce-ink">{t('farmaciaApp.convenios.pendientesTitulo')}</p>
        {pendientes.length === 0 ? (
          <p className="text-sm text-hce-muted">{t('farmaciaApp.convenios.sinPendientes')}</p>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {pendientes.map((p) => (
              <div key={p.ref} className="space-y-2 px-3 py-2.5 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="min-w-0 flex-1">
                    <span className="block text-hce-ink">
                      {p.contactName} · {formatoUsd(p.amountCents ?? 0)}
                    </span>
                    <span className="block text-xs text-hce-muted">{p.nota}</span>
                  </span>
                  <Button size="sm" onClick={() => abrirFacturar(p)}>
                    {t('farmaciaApp.convenios.facturar')}
                  </Button>
                </div>
                {facturando === p.ref && (
                  <div className="flex flex-wrap items-end gap-2 rounded-md bg-hce-paper p-2.5">
                    <div>
                      <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.convenios.convenio')}</label>
                      <select
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={convenioElegido[p.ref] ?? ''}
                        onChange={(e) => setConvenioElegido((prev) => ({ ...prev, [p.ref]: Number(e.target.value) }))}
                      >
                        <option value="">{t('farmaciaApp.convenios.elegir')}</option>
                        {convenios.filter((c) => c.puedeFacturar).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="min-w-40 flex-1">
                      <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.convenios.paciente')}</label>
                      <Input value={paciente} onChange={(e) => setPaciente(e.target.value)} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-hce-muted">{t('farmaciaApp.convenios.prefactura')}</label>
                      <Input className="w-32" value={prefacturaRef} onChange={(e) => setPrefacturaRef(e.target.value)} />
                    </div>
                    <Button size="sm" disabled={enviando} onClick={() => void confirmarFacturar(p)}>
                      {enviando ? t('farmaciaApp.comprobantes.enviando') : t('farmaciaApp.comprobantes.confirmar')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setFacturando(undefined)}>
                      {t('farmaciaApp.catalogo.cancelar')}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-hce-ink">{t('farmaciaApp.convenios.emitidasTitulo')}</p>
        {facturas.length === 0 ? (
          <p className="text-sm text-hce-muted">{t('farmaciaApp.convenios.sinEmitidas')}</p>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {facturas.map((f) => (
              <div key={f.invoiceId} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                <span className="text-hce-ink">
                  #{f.invoiceId} · {f.convenioName}
                </span>
                <span className="text-hce-muted">{formatoUsd(f.totalCents)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
